import { Lesson, TrainingWithLessons } from '@/app/entities/Training'
import { formatDuration } from '@/app/utils/formatDuration'
import { withCdn } from '@/app/utils/withCdn'
import { Button } from '@/view/components/ui/button'
import { toast } from '@/view/components/ui/use-toast'
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Edit } from 'iconsax-react'

import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { DeleteLesson } from '../components/DeleteLesson'
import { twMerge } from 'tailwind-merge'

const columnHelper = createColumnHelper<Lesson>()

interface GetTraningDetailsTableProps {
  handleEditClass: (lessonId: string) => void
}

const getTraningDetailsTable = ({
  handleEditClass,
}: GetTraningDetailsTableProps) => [
  columnHelper.accessor('name', {
    id: 'name',
    header: () => (
      <div className="hidden pl-8 text-left text-xl md:block">Treinamento</div>
    ),
    cell: ({ row }) => (
      <div className="hidden py-5 pl-5 text-left md:block">
        <div className="flex items-center gap-5 text-xl font-semibold">
          {row.original?.thumbnail_url && (
            <img
              src={withCdn(row.original?.thumbnail_url)}
              alt={row.original?.name}
              className={twMerge(
                'h-[61px] w-[125px] rounded-md bg-gray-200',
                !row.original?.thumbnail_url && 'contrast-50',
              )}
            />
          )}
          {row.original.name}
        </div>
      </div>
    ),
  }),
  columnHelper.accessor('duration', {
    id: 'numberOfClasses',
    header: () => (
      <div className="hidden text-right text-xl md:block">Duração</div>
    ),
    cell: ({ row }) => (
      <div className="hidden text-right text-xl font-semibold md:block">
        {formatDuration(row?.original?.duration || 0)}
      </div>
    ),
  }),
  columnHelper.display({
    id: 'edit',
    header: () => <div className="pr-8 text-right text-xl">Opções</div>,
    cell: ({ row }) => (
      <div className="flex items-center justify-end gap-2 pr-8 text-xl font-semibold">
        <Button
          size="icon"
          variant="outline"
          className="h-11 w-11"
          onClick={(e) => {
            e.stopPropagation()
            handleEditClass(row?.original?._id)
          }}
        >
          <Edit size={24} color="#0C0E13" strokeWidth={1.5} />
        </Button>
        <DeleteLesson lessonId={row?.original?._id} />
      </div>
    ),
  }),
]

interface UseTrainingDetailsTableProps {
  lessons: TrainingWithLessons['lessons']
  trainingId: string
}

export const useTrainingDetailsTable = ({
  lessons,
  trainingId,
}: UseTrainingDetailsTableProps) => {
  const navigate = useNavigate()

  const handleEditClass = useCallback(
    (lessonId: string) => {
      if (!lessonId) {
        toast({
          title: 'Erro ao editar aula',
          description: 'Aula não encontrada',
        })
        return
      }
      navigate(`/conteudos/gerenciar/treinamentos/${trainingId}/${lessonId}`)
    },
    [navigate, trainingId],
  )

  const columns = useMemo(
    () => getTraningDetailsTable({ handleEditClass }),
    [handleEditClass],
  )

  return useReactTable({
    columns,
    data: lessons,
    getCoreRowModel: getCoreRowModel(),
  })
}
